import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AccessExpiryBanner from '../components/AccessExpiryBanner';
import ActivateBookForm from '../components/ActivateBookForm';
import BookShelf from '../components/BookShelf';
import Modal from '../components/Modal';
import { ErrorBanner, InfoBanner, Toast } from '../components/Feedback';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

/**
 * Module 6 — the student's dashboard. Deliberately lightweight: the guideline reserves
 * assessments and progress tracking for Phase 3, so there is nothing here that implies
 * either. What it does show is what a student came for — their books — and the one
 * thing that ever needs acting on, which is a window about to close.
 *
 * <p>Anything derived from "what you opened recently" is left out on purpose: that
 * would be progress tracking built out of the access log, which Phase 1 excludes.
 */
export default function StudentDashboardPage() {
  const { user } = useAuth();
  const [library, setLibrary] = useState(null);
  const [showActivate, setShowActivate] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  async function load() {
    setLibrary(await api.studentLibrary());
  }

  useEffect(() => {
    load().catch(setError);
  }, []);

  const books = library?.books ?? [];
  const expiring = books.filter((book) => book.accessStatus !== 'ACTIVE');
  const open = books.filter((book) => book.accessible);

  return (
    <>
      <div className="page-head row-between">
        <div>
          <h1>Hello, {user?.fullName?.split(' ')[0]}</h1>
          <p>
            {books.length === 0
              ? 'Activate a book to start reading.'
              : `${open.length} book${open.length === 1 ? '' : 's'} ready to read.`}
          </p>
        </div>
        {books.length > 0 && (
          <button type="button" className="btn btn-primary" onClick={() => setShowActivate(true)}>
            Activate a book
          </button>
        )}
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {/* The one thing that ever needs acting on. */}
      {expiring.map((book) => (
        <AccessExpiryBanner
          key={book.titleId}
          status={book.accessStatus}
          daysRemaining={book.daysRemaining}
          what={book.name}
          action="Ask your teacher about renewing it."
        />
      ))}

      {library === null && !error && <div className="empty">Loading…</div>}

      {library !== null && books.length === 0 && (
        <section className="card card-pad" style={{ maxWidth: 460 }}>
          <h2 style={{ fontSize: 'var(--fs-md)', marginTop: 0, marginBottom: 'var(--s0)' }}>
            Activate your first book
          </h2>
          <p className="muted small" style={{ marginTop: 0 }}>
            Your teacher gives you a code for each book. Entering it here adds that book
            to your library.
          </p>
          <ActivateBookForm
            onActivated={(result) => {
              setToast(`${result.book.name} added to your library`);
              load().catch(setError);
            }}
            autoFocus
          />
        </section>
      )}

      {books.length > 0 && (
        <>
          <div className="row-between" style={{ marginBottom: 'var(--s1)' }}>
            <h2 className="eyebrow" style={{ margin: 0 }}>My books</h2>
            <Link to="/library" className="small">
              Open my library
            </Link>
          </div>
          <BookShelf books={books} />
        </>
      )}

      {books.length > 0 && open.length === 0 && (
        <InfoBanner>
          Every book in your library has expired. Ask your teacher about renewing them.
        </InfoBanner>
      )}

      {showActivate && (
        <Modal
          title="Activate a book"
          subtitle="Enter the code your teacher gave you."
          width={460}
          onClose={() => setShowActivate(false)}
        >
          <ActivateBookForm
            onActivated={(result) => {
              setShowActivate(false);
              setToast(`${result.book.name} added to your library`);
              load().catch(setError);
            }}
            autoFocus
          />
        </Modal>
      )}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2600)} />
    </>
  );
}
